import React, {useEffect, useRef, useState} from 'react';
import Modal from "react-bootstrap/Modal";
import {useDispatch, useSelector} from "react-redux";
import {clearErrors, setUpdateMachineId, updateMachine, getEmployeeOptions, getEmployeeOptionsByFirm} from "../../actions/machine";
import Button from "react-bootstrap/Button";
import useActiveFirm from '../../hooks/useActiveFirm';

const MachineUpdateForm = () => {

    const dispatch = useDispatch();
    const { firmId } = useActiveFirm();

    const {update_machine, error, loading, currentPage, firm_options, employee_options} = useSelector(state => state.machine);
    
    useEffect(() => {
        if (firmId) {
            dispatch(getEmployeeOptionsByFirm(firmId));
        } else {
            dispatch(getEmployeeOptions());
        }
    }, [dispatch, firmId]);

    // Load employees for the selected firm when update_machine changes
    useEffect(() => {
        if (update_machine && update_machine.firm_id) {
            dispatch(getEmployeeOptionsByFirm(update_machine.firm_id));
        }
    }, [update_machine, dispatch]);

    useEffect(() => {
        if (update_machine) {
            setInputs({
                name: update_machine.name || '',
                model: update_machine.model || '',
                serial_number: update_machine.serial_number || '',
                firm_id: update_machine.firm_id || '',
                operator_id: update_machine.operator_id || '',
                description: update_machine.description || '',
                status: update_machine.status || 'active'
            });
        }
    }, [update_machine]);

    const formRef = useRef();

    const defaultValues = {
        name: '',
        model: '',
        serial_number: '',
        firm_id: '',
        operator_id: '',
        description: '',
        status: 'active'
    };

    const [inputs, setInputs] = useState(defaultValues);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (error && loading === false) {
            const errorsItems = error?.errors;
            let errorObject = {};
            if (error && error?.errors) {
                Object.keys(error?.errors).forEach((item) => errorObject[item] = errorsItems[item][0]);
            }
            setErrors(errorObject);
        }
    }, [error]);

    const handleChange = e => {
        setErrors({});
        e.target.oldValue = e.target.value;
        const newInputs = {...inputs, [e.target.name]: e.target.value};
        setInputs(newInputs);
        
        // If firm is changed, filter employees by that firm
        if (e.target.name === 'firm_id') {
            if (e.target.value) {
                dispatch(getEmployeeOptionsByFirm(e.target.value));
            } else {
                dispatch(getEmployeeOptions()); // Load all employees if no firm selected
            }
            // Reset operator selection when firm changes
            setInputs(prevState => ({...prevState, [e.target.name]: e.target.value, operator_id: ''}));
        }
    };
    
    const onClose = () => {
        try {
            dispatch(clearErrors());
            setInputs(defaultValues);
            setErrors({});
            dispatch(setUpdateMachineId(null));
        } catch (error) {
            console.error('Error in onClose:', error);
            // Fallback: just close the modal
            dispatch(setUpdateMachineId(null));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const data = {
            name: inputs.name,
            model: inputs.model,
            serial_number: inputs.serial_number,
            firm_id: firmId || inputs.firm_id, // Ensure firm_id is always from global active firm
            operator_id: inputs.operator_id || null,
            description: inputs.description,
            status: inputs.status
        };
        
        const filters = {};
        if (firmId) {
            filters.firm_id = firmId;
        }
        
        await dispatch(updateMachine(update_machine.id, data, currentPage, filters));
    };

    let condition = false;
    if (update_machine !== null)
        condition = true;

    return (
        <div>
            <Modal show={condition} onHide={onClose} size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>Uskuna tahrirlash</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3 pb-4 mx-3">
                    <form onSubmit={handleSubmit} ref={formRef}>
                        
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="name">Nomi <span className="text-danger">*</span></label>
                                    <input
                                        name="name"
                                        className={'form-control'}
                                        type="text"
                                        value={inputs?.name || ''}
                                        placeholder={'Uskuna nomini kiriting'}
                                        onChange={handleChange}
                                        onKeyUp={e => e.target.oldValue = e.target.value}
                                    />
                                    <div className="error">
                                        {errors?.name}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="model">Model</label>
                                    <input
                                        name="model"
                                        className={'form-control'}
                                        type="text"
                                        value={inputs?.model || ''}
                                        placeholder={'Model nomini kiriting'}
                                        onChange={handleChange}
                                        onKeyUp={e => e.target.oldValue = e.target.value}
                                    />
                                    <div className="error">
                                        {errors?.model}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="serial_number">Seriya raqami</label>
                                    <input
                                        name="serial_number"
                                        className={'form-control'}
                                        type="text"
                                        value={inputs?.serial_number || ''}
                                        placeholder={'Seriya raqamini kiriting'}
                                        onChange={handleChange}
                                        onKeyUp={e => e.target.oldValue = e.target.value}
                                    />
                                    <div className="error">
                                        {errors?.serial_number}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="firm_id">Firma <span className="text-danger">*</span></label>
                                    <select name="firm_id"
                                            value={inputs?.firm_id || ''}
                                            id="firm_id"
                                            className="form-control"
                                            onChange={handleChange}
                                            onKeyUp={e => e.target.oldValue = e.target.value}
                                    >
                                        <option value="">Firma tanlang</option>
                                        {firm_options && firm_options.map((item, index) => (
                                            <option key={index} value={item.id}>{item.name}</option>
                                        ))}
                                    </select>
                                    <div className="error">
                                        {errors?.firm_id}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="operator_id">
                                        Operator 
                                        {inputs.firm_id && (
                                            <small className="text-muted"> (tanlangan firmaga tegishli)</small>
                                        )}
                                    </label>
                                    <select name="operator_id"
                                            value={inputs?.operator_id || ''}
                                            id="operator_id"
                                            className="form-control"
                                            onChange={handleChange}
                                            onKeyUp={e => e.target.oldValue = e.target.value}
                                            disabled={!inputs.firm_id}
                                    >
                                        <option value="">
                                            {inputs.firm_id ? 'Operator tanlang' : 'Avval firma tanlang'}
                                        </option>
                                        {employee_options && employee_options.map((item, index) => (
                                            <option key={index} value={item.id}>
                                                {item.first_name} {item.last_name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="error">
                                        {errors?.operator_id}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="status">Holati <span className="text-danger">*</span></label>
                                    <select name="status"
                                            value={inputs?.status || 'active'}
                                            id="status"
                                            className="form-control"
                                            onChange={handleChange}
                                            onKeyUp={e => e.target.oldValue = e.target.value}
                                    >
                                        <option value="active">Faol</option>
                                        <option value="maintenance">Ta'mirlash</option>
                                        <option value="inactive">Nofaol</option>
                                    </select>
                                    <div className="error">
                                        {errors?.status}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="description">Tavsif</label>
                            <textarea
                                name="description"
                                className={'form-control'}
                                rows="3"
                                value={inputs?.description || ''}
                                placeholder={'Uskuna haqida qo\'shimcha ma\'lumot'}
                                onChange={handleChange}
                                onKeyUp={e => e.target.oldValue = e.target.value}
                            />
                            <div className="error">
                                {errors?.description}
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            <Button className="w-50" variant="secondary" type="button" onClick={onClose}>
                                Bekor qilish
                            </Button>
                            <Button className="w-50" variant="primary" type="submit" disabled={loading}>
                                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
                            </Button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MachineUpdateForm; 